import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useSearchParams } from "react-router-dom";

import { Field } from "../services/collectionFields";

interface QueryParamsState {
  filters: Record<string, string[]>;
  sort: Record<string, string | undefined>;
  search: { keywords?: string };
  pagination: { page?: number; limit?: number };
}

interface UpdateQueryParams {
  setSearch: (keywords: string) => void;
  setFilters: (filters: Record<string, string[]>) => void;
  setPagination: (page?: number, limit?: number) => void;
  setSort: (sort: Record<string, string | undefined>) => void;
}

export const useQueryParamsToState = (): QueryParamsState &
  UpdateQueryParams & { toQueryParamsUrl: () => string } => {
  const [searchParams] = useSearchParams();

  const setSearch = (keywords: string) => {
    searchParams.delete("search");

    if (keywords) searchParams.append("search", keywords);
  };

  const setFilters = (filters: Record<string, string[]>) => {
    searchParams.delete("filter");

    if (!isEmpty(filters)) {
      Object.keys(filters).forEach((field) => {
        searchParams.append(`filter`, `${field}:::${filters[field].join(",")}`);
      });
    }
  };

  const setPagination = (page?: number, limit?: number) => {
    searchParams.delete("page");
    searchParams.delete("limit");

    searchParams.append("page", page?.toString() || "1");
    searchParams.append("limit", limit?.toString() || "50");
  };

  const setSort = (sort: Record<string, string | undefined>) => {
    searchParams.delete("sort");

    if (!isEmpty(sort)) {
      Object.keys(sort).forEach((field) => {
        if (!sort[field]) searchParams.delete("sort");
        else
          searchParams.append(
            "sort",
            sort[field] === "ascend" ? field : `-${field}`
          );
      });
    }
  };

  const toQueryParamsUrl = (): string => searchParams.toString();

  let filters = {},
    sort = {},
    pagination = {},
    search = {};
  searchParams.forEach((value, key) => {
    // filters
    if (key === "filter") {
      const fieldName = value.split(":::")[0];
      filters = { ...filters, [fieldName]: value.split(":::")[1].split(",") };
    }
    // sort
    if (key === "sort") {
      if (value.startsWith("-"))
        sort = { ...sort, [value.slice(1)]: "descend" };
      else sort = { ...sort, [value]: "ascend" };
    }

    // pagination and search
    if (["page", "limit", "search"].includes(key)) {
      pagination = { ...pagination, [key]: value };
    }

    // search
    if (key === "search") search = { ...search, keywords: value };
  });

  return {
    filters,
    sort,
    pagination,
    search,
    toQueryParamsUrl,
    setFilters,
    setPagination,
    setSearch,
    setSort,
  };
};

const directusOperators: Record<string, string> = {
  "<": "_lt",
  "<=": "_lte",
  ">": "_gt",
  ">=": "_gte",
  "=": "_eq",
  "!=": "_neq",
};

const yearFilterIntervals: Record<number, string> = {
  1919: "<1919",
  1945: "1919-1945",
  1960: "1946-1960",
  1970: "1961-1970",
  1980: "1971-1980",
  1985: "1981-1985",
  1990: "1986-1990",
  1995: "1991-1995",
  2000: "1996-2000",
  2005: "2001-2005",
  2010: "2006-2010",
  2015: "2011-2015",
};

export const transformToApiQueryParams = (
  fields: Field[],
  urlParams: URLSearchParams,
  exclude?: string[]
) => {
  const apiParams = new URLSearchParams();
  let filterParamValue = {};
  urlParams.forEach((value, key) => {
    if (!(exclude && exclude.includes(key))) {
      if (key === "filter") {
        const [field, fieldValue] = value.split(":::");
        const foundField = fields.find((f) => f.name === field);
        if (foundField && foundField?.name === "AllCheckboxes") {
          const filterValues = fieldValue.split(",");
          
          // Separate negated and regular values
          const negatedValues = filterValues.filter(v => v.startsWith("NOT:")).map(v => v.substring(4));
          const regularValues = filterValues.filter(v => !v.startsWith("NOT:"));
          
          // If we have negated values, we need to structure the filter differently
          if (negatedValues.length > 0) {
            let finalConditions: any[] = [];
            
            // Check if we're negating empty values
            const negatesEmpty = negatedValues.includes("Vide (Sans valeur)");
            const nonEmptyNegatedValues = negatedValues.filter(v => v !== "Vide (Sans valeur)");
            
            if (negatesEmpty && nonEmptyNegatedValues.length > 0) {
              // If we're negating empty AND other values, only show non-empty records that don't contain the other values
              const andConditions: any[] = [{ AllCheckboxes: { _nempty: true } }];
              nonEmptyNegatedValues.forEach((v) => {
                andConditions.push({ AllCheckboxes: { _ncontains: v } });
              });
              finalConditions.push({ _and: andConditions });
            } else if (negatesEmpty) {
              // If we're only negating empty values, show only non-empty records
              finalConditions.push({ AllCheckboxes: { _nempty: true } });
            } else {
              // If we're not negating empty values, show records that are empty OR don't contain any of the negated values
              finalConditions.push({ AllCheckboxes: { _empty: true } });
              
              if (nonEmptyNegatedValues.length > 0) {
                // Create AND condition for all negated values (record must not contain any of them)
                const andConditions: any[] = [];
                nonEmptyNegatedValues.forEach((v) => {
                  andConditions.push({ AllCheckboxes: { _ncontains: v } });
                });
                finalConditions.push({ _and: andConditions });
              }
            }
            
            // If we also have regular (positive) values, combine them with AND
            if (regularValues.length > 0) {
              let positiveConditions: any[] = [];
              
              regularValues.forEach((v) => {
                if (v === "Vide (Sans valeur)") {
                  positiveConditions.push({ AllCheckboxes: { _empty: true } });
                } else {
                  positiveConditions.push({ AllCheckboxes: { _icontains: v } });
                }
              });
              
              // Combine positive and negative conditions using AND
              filterParamValue = {
                ...filterParamValue,
                _and: [
                  { _or: finalConditions },
                  { _or: positiveConditions }
                ]
              };
            } else {
              // Only negated values
              filterParamValue = {
                ...filterParamValue,
                _or: finalConditions
              };
            }
          } else {
            // Only regular (positive) values - use the original logic
            let allCheckboxesFilter: any[] = [];
            
            regularValues.forEach((v) => {
              if (v === "Vide (Sans valeur)") {
                allCheckboxesFilter = [...allCheckboxesFilter, { _empty: true }];
              } else {
                allCheckboxesFilter = [...allCheckboxesFilter, { _icontains: v }];
              }
            });
            
            filterParamValue = {
              ...filterParamValue,
              AllCheckboxes: { _and: allCheckboxesFilter },
            };
          }
        } else if (foundField && foundField?.name === "Usages") {
          let usagesFilter: any[] = [];
          // Strip suffix from text input (*** for contains, === for exact)
          let processedFieldValue = fieldValue;
          if (fieldValue.endsWith("***") || fieldValue.endsWith("===")) {
            processedFieldValue = fieldValue.slice(0, -3);
          }
          const filterValues = processedFieldValue.split(",");
          if (filterValues.includes("Vide (Sans valeur)")) {
            usagesFilter = [...usagesFilter, { _empty: true }];
          }

          const withoutEmptyValues = filterValues.filter(
            (v) => v !== "Vide (Sans valeur)"
          );
          withoutEmptyValues.forEach((v) => {
            const trimmedValue = v.trim();
            if (trimmedValue === "Garage / Box") {
              // Special case: search for entries containing "garage" or "box"
              usagesFilter = [
                ...usagesFilter,
                { _icontains: "garage" },
                { _icontains: "box" },
              ];
            } else {
              usagesFilter = [...usagesFilter, { _icontains: trimmedValue }];
            }
          });
          filterParamValue = {
            ...filterParamValue,
            Usages: { _or: usagesFilter },
          };
        } else if (foundField && foundField.ui === "datetime") {
          if (fieldValue.includes("***")) {
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _eq: new Intl.DateTimeFormat("fr-CA", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                }).format(
                  dayjs(
                    `${fieldValue.replaceAll("*", "")}`,
                    "DD/MM/YYYY"
                  ).toDate()
                ),
              },
            };
          } else {
            try {
              filterParamValue = {
                ...filterParamValue,
                [field]: {
                  _between: fieldValue.split(",").map((v) =>
                    new Intl.DateTimeFormat("fr-CA", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    }).format(dayjs(v.trim() as string, "DD/MM/YYYY").toDate())
                  ),
                },
              };
            } catch (error) {
              console.error(error);
            }
          }
        } else if (
          foundField?.type !== "float" &&
          foundField?.type !== "integer" &&
          foundField?.type !== "date" &&
          fieldValue.split(",").length === 1 &&
          (fieldValue.endsWith("***") || fieldValue.endsWith("==="))
        ) {
          const isExactSearch = fieldValue.endsWith("===");
          const suffixLength = isExactSearch ? 3 : 3; // Both suffixes are 3 characters
          
          if (foundField?.name === "AnneeConstruction" && !isExactSearch) {
            const year = +fieldValue.slice(0, fieldValue.length - suffixLength);

            let filter = year.toString();
            const keys = Object.keys(yearFilterIntervals);
            for (let i = 0; i < keys.length; i++) {
              const y = +keys[i];
              if (year <= +y) {
                filter = yearFilterIntervals[+y];
                break;
              }
            }
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _in: [filter, year.toString()],
              },
            };
          } else if (foundField?.name === "NoParcelle" || isExactSearch) {
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _eq: fieldValue.slice(0, fieldValue.length - suffixLength),
              },
            };
          } else {
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _icontains: fieldValue.slice(0, fieldValue.length - suffixLength),
              },
            };
          }
        } else if (
          foundField?.type === "integer" ||
          foundField?.type === "float"
        ) {
          if (["<", ">", "="].includes(fieldValue.trim()[0])) {
            const operator = fieldValue.trim()[0];
            const value = fieldValue
              .split("")
              .slice(1)
              .join("")
              .replaceAll("*", "");
            if (value) {
              filterParamValue = {
                ...filterParamValue,
                [field]: {
                  [directusOperators[operator]]: value,
                },
              };
            }
          } else if (fieldValue === ",") {
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _empty: true,
              },
            };
          } else {
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _between: fieldValue,
              },
            };
          }
        } else {
          if (field === "Year") {
            const [minYear, maxYear] = fieldValue
              .split(",")
              .map((v) => parseInt(v.trim()));
            const allYears = [];
            for (let i = minYear; i <= maxYear; i += 1) {
              allYears.push(i.toString());
            }
            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _in: allYears,
              },
            };
          } else if (foundField?.name === "AnneeConstruction") {
            const [min, max] = fieldValue.split(",").map((v) => +v);

            const values = Object.values(yearFilterIntervals);
            const years = [];
            for (let filterYear = min; filterYear <= max; filterYear++) {
              years.push(filterYear.toString());

              for (let index = 0; index < values.length; index++) {
                const interval = values[index];
                const [start, end] = interval.split("-").map((v) => +v);
                if(isNaN(start) && filterYear < 1919) {
                  years.push(interval);
                }
                  
                if (start <= filterYear && filterYear <= end) {
                  years.push(interval);
                }
              }
            }

            filterParamValue = {
              ...filterParamValue,
              [field]: {
                _in: years,
              },
            };
          } else {
            const filterValues = fieldValue.split(",").map((v) => v.trim());

            filterParamValue = {
              ...filterParamValue,
              [field]: filterValues.includes("Vide (Sans valeur)")
                ? {
                    _or: [
                      { _empty: true },
                      {
                        _in: filterValues,
                      },
                    ],
                  }
                : {
                    _in: filterValues,
                  },
            };
          }
        }
      } else if (key === "id") {
        filterParamValue = {
          ...filterParamValue,
          id: {
            _eq: value,
          },
        };
      } else {
        apiParams.append(key, value);
      }
    }
  });
  // outside to not repeat the "filter" parameter
  apiParams.append("filter", JSON.stringify(filterParamValue));

  if (!urlParams.has("page")) apiParams.append("page", "1");

  if (!urlParams.has("limit")) apiParams.append("limit", "50");

  return apiParams;
};
